<script setup lang="ts">
import { InfoTip } from '@it-tools/shell'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { fromInches, type LengthUnit, type ScreenMetrics } from '../model/ppi'

const props = defineProps<{
  metrics: ScreenMetrics | null
  /** The unit the visitor typed; physical sizes are reported back in it. */
  unit: LengthUnit
}>()

const { t, n } = useI18n()

interface Row {
  readonly key: string
  readonly label: string
  readonly value: string
  readonly help: string | null
}

const unitLabel = computed(() => t(`unitShort.${props.unit}`))

const rows = computed<Row[]>(() => {
  const metrics = props.metrics
  if (!metrics) return []

  const ratio = `${n(metrics.aspectRatio.width)}:${n(metrics.aspectRatio.height)}`

  return [
    {
      key: 'dotPitch',
      label: t('results.dotPitch'),
      value: t('results.mm', { value: n(metrics.dotPitchMm, { maximumFractionDigits: 3 }) }),
      help: t('results.dotPitchHelp'),
    },
    {
      key: 'diagonalPixels',
      label: t('results.diagonalPixels'),
      value: t('results.px', { value: n(metrics.diagonalPixels, { maximumFractionDigits: 0 }) }),
      help: null,
    },
    {
      key: 'screenSize',
      label: t('results.screenSize'),
      value: t('results.dimensions', {
        width: n(fromInches(metrics.widthInches, props.unit), { maximumFractionDigits: 2 }),
        height: n(fromInches(metrics.heightInches, props.unit), { maximumFractionDigits: 2 }),
        unit: unitLabel.value,
      }),
      help: null,
    },
    {
      key: 'aspectRatio',
      label: t('results.aspectRatio'),
      // An approximated ratio must say so rather than claim 1366×768 is 16:9.
      value: metrics.aspectRatio.exact ? ratio : t('results.approximate', { value: ratio }),
      help: null,
    },
    {
      key: 'megapixels',
      label: t('results.totalPixels'),
      value: t('results.megapixels', {
        value: n(metrics.megapixels, { maximumFractionDigits: 2 }),
        total: n(metrics.totalPixels, { maximumFractionDigits: 0 }),
      }),
      help: null,
    },
    {
      key: 'pixelsPerSquareInch',
      label: t('results.pixelsPerSquareInch'),
      value: t('results.pxSquared', {
        value: n(metrics.pixelsPerSquareInch, { maximumFractionDigits: 0 }),
      }),
      help: null,
    },
    {
      key: 'density',
      label: t('results.density'),
      value: t(`density.${metrics.densityClass}`),
      help: t('results.densityHelp'),
    },
  ]
})
</script>

<template>
  <section class="metrics" aria-labelledby="metrics-heading">
    <h2 id="metrics-heading" class="visually-hidden">{{ t('results.heading') }}</h2>

    <template v-if="metrics">
      <p class="metrics__headline">
        <span class="metrics__ppi mono">{{ n(metrics.ppi, { maximumFractionDigits: 2 }) }}</span>
        <span class="metrics__ppi-label">{{ t('results.ppi') }}</span>
      </p>

      <dl class="metrics__list">
        <div v-for="row in rows" :key="row.key" class="metrics__row">
          <dt>
            {{ row.label }}
            <InfoTip v-if="row.help" :label="row.label" :text="row.help" />
          </dt>
          <dd class="mono">{{ row.value }}</dd>
        </div>
      </dl>
    </template>

    <p v-else class="metrics__empty">{{ t('results.empty') }}</p>
  </section>
</template>

<style scoped>
.metrics {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
}

.metrics__headline {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2);
  margin: 0;
  padding: var(--space-6) var(--space-6) var(--space-4);
}

.metrics__ppi {
  font-size: 2.75rem;
  font-weight: 600;
  line-height: 1;
  color: var(--color-accent);
}

.metrics__ppi-label {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.metrics__list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: 0;
  margin: 0;
}

.metrics__row {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--color-border);
}

dt {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

dd {
  margin: 0;
  font-size: var(--text-lg);
}

.metrics__empty {
  margin: 0;
  padding: var(--space-6);
  color: var(--color-text-muted);
}
</style>
